import { Fragment, MouseEvent } from 'react';
import { dateFormat } from './utils';
import {
  Document,
  FullSegment,
  MoCxlGroupData,
  MoDetails,
  MoInfoData,
  MoSupplier,
  MoSupplierData,
  MoTableData,
  MoTablePaymentData,
  MyOrder,
  Pax,
  PaymentTrResponse,
} from 'ts/shared/interfaces/MOResponse';
import i18n from 'i18next';
import { faBitcoinSign, faCheckSquare, faCopy, faCreditCard, faCreditCardAlt, faDesktop, faEnvelope, faExclamationCircle, faHand, faList, faLock, faPhoneSquare, faRightLeft, faUser } from '@fortawesome/free-solid-svg-icons';
import { faMoneyBillAlt } from '@fortawesome/free-regular-svg-icons';
import { faCcVisa, faGoogle, faMaxcdn } from '@fortawesome/free-brands-svg-icons';
import { getPayment, getStatus } from 'constants/mo';
import { formatPrice, replaceHtmlToStr, showSuccess } from 'utils/utils';
import ReactCountryFlag from 'react-country-flag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import { closeRequest, downloadDocument } from 'services/myOrders';
import MoDeleteDocument from '../../components/shared/MyOrders/MoActions/MoDeleteDocument';
import Tooltip from 'components/ui/Tooltip/Tooltip';

export const formatDetailsGroup = (myOrder: MyOrder) => {
  return {
    checkIn: dateFormat(myOrder.orderRow.checkIn, 'dd MMM yyyy')?.toUpperCase(),
    checkInDate: new Date(myOrder.orderRow.checkIn),
    checkOut: dateFormat(myOrder.orderRow.checkOut, 'dd MMM yyyy')?.toUpperCase(),
    nights: myOrder.fullSegment.numberOfNights + '',
    roomsCount: myOrder.fullSegment.numberOfRooms + '',
    beproHtml: (
      <span>
        {myOrder.orderRow.orderId}.
        <span className="text-bold">
          {myOrder.orderRow.segmentId}
          <FontAwesomeIcon
            icon={faCopy}
            size="xs"
            color="var(--color-warning)"
            className="ml-5 cur-poi"
            onClick={() => {
              navigator.clipboard.writeText(`${myOrder.orderRow.segmentId}`);
              showSuccess(i18n.t('mo.clipboard_success'));
            }}
          />
        </span>
      </span>
    ),
    docket: myOrder.orderRow.docket,
    dateC: dateFormat(myOrder.orderRow.dateC, 'dd/MM/yyyy HH:mm'),
    totalHtml: `<span class="text-success text-bold">${formatPrice(
      myOrder.orderRow.price,
      myOrder.orderRow.currency
    )}</span>`,
    timeLimit: myOrder.fullSegment.itemAddress,
    address: myOrder.fullSegment.itemAddress,
    phone: myOrder.fullSegment.itemPhone,
    zip: myOrder.fullSegment.itemZip,
  };
};

export const formatPaxesTable = (paxes: Pax[]) => {
  const getAge = (dob: string): number => {
    if (!dob || typeof dob !== 'string') {
      return 0;
    }

    const today = new Date();
    const birthDate = new Date(dob);

    if (isNaN(birthDate.getTime())) {
      return 0;
    }

    if (birthDate > today) {
      return 0;
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return Math.max(0, age);
  };

  return paxes.map((pax) => {
    const age = getAge(pax.dob);
    const ageStr = age < 150 ? ' (' + age + ')' : '';

    const skipDob = pax.dob?.includes('0001') || pax.dob?.includes('1900');
    return {
      index: pax.paxCode + '',
      title: pax.paxTitle,
      fn: pax.firstName,
      ln: pax.lastName,
      gender: pax.gender,
      birthday: skipDob ? '' : dateFormat(pax.dob, 'dd/MMM/yyyy', 'en') + ageStr,
      passport: pax.passportNumber,
      email: pax.email1,
      phone: pax.phone1,
    };
  });
};

export const formatFlightTable = (segment: FullSegment) => {
  return segment.details.map((detail) => {
    return {
      pnr: detail.itemPNR,
      flight: detail.item.code + ' ' + detail.flightNumber,
      flightHtml: `<span class="text-bold mo-status mo-status-secondary">${detail.item.code} ${detail.flightNumber}</span> `,
      legs: detail.legNumber + '',
      paxes: detail.numberOfPax + '',
      departure: detail.departure.port.code + ' ' + detail.departure.port.name,
      departureHtml: `<span class="text-bold">${detail.departure.port.code}</span> ${detail.departure.port.name}`,
      depClassOfService: detail.departure.classOfService.code,
      depDateTime: dateFormat(detail.departure.date, 'dd/MMM/yyyy') + ' ' + detail.departure.time,
      arrival: detail.arrival.port.code + ' ' + detail.arrival.port.name,
      arrivalHtml: `<span class="text-bold">${detail.arrival.port.code}</span> ${detail.arrival.port.name}`,
      arrClassOfService: detail.arrival.classOfService.code,
      arrDateTime: dateFormat(detail.arrival.date, 'dd/MMM/yyyy') + ' ' + detail.arrival.time,
      status: detail.sysStatus.code,
      statusHtml: `<span class="mo-status ${getStatus(detail.sysStatus.code)}">${detail.sysStatus.code
        }</span>`,
    };
  });
};

export const formatCxlGroup = (segment: FullSegment): MoCxlGroupData => {
  const data = segment.rooms.map((room) => {
    return {
      cxlHtml: replaceHtmlToStr(room.cancellationNote),
      supplier: replaceHtmlToStr(room.suppCancellationNote),
      limitDateHtml: `<span class="text-bold">${dateFormat(
        room.cancellationDateLimit,
        'dd MMM yyyy'
      )?.toUpperCase()}</span>`,
    };
  });

  return data[0];
};

export const formatInfoGroup = ({ user, orderRow, branchPhone, order, fullSegment }: MyOrder): MoInfoData => {
  const userEmail = user?.user_email || '';

  const namesStrHtl = fullSegment.rooms
    ?.map((room) => room.paxes.map((pax) => pax.firstName + ' ' + pax.lastName).join('%0A'))
    .join('%0A') || '';
  const namesStrRest = order.paxes.map((pax) => pax.firstName + ' ' + pax.lastName).join('%0A');
  const namesStr = fullSegment.rooms?.length ? namesStrHtl : namesStrRest;

  const checkIn = dateFormat(fullSegment.checkIn, 'd-MMM-yy');
  const subject = `Reminder. REF: ${orderRow.suppPnrWithOutLock} Check In: ${checkIn} Department: ${orderRow.departmentName}`;
  const body = `REF: ${orderRow.suppPnrWithOutLock}%0AHotel Name: ${fullSegment.itemDesc}%0ACheck In: ${checkIn}%0APassengers names:%0A${namesStr}%0A%0ACompany: ${order.companyId}%0ABranch: ${orderRow?.branchName + ' - ' + order.branchId}%0AUser: ${orderRow?.agentName + ' - ' + orderRow?.userCode}%0ABePro Segment: ${orderRow?.segmentId}%0A%0AThank you`;
  const userMailLabel = `mailto:${userEmail}?subject=${subject}&body=${body}GMAILhttps://mail.google.com/mail/u/0/?view=cm&fs=1&su=${subject}&body=${body}&to=${userEmail}`;
  const userMailJsx = (
    <span className='flex-row'>
      <a className="btn btn-primary btn-xsm mr-5" href={userMailLabel?.split('GMAIL')[0]} style={{ width: 100 }}>
        <FontAwesomeIcon icon={faEnvelope} className="mr-5" />
        Reminder
      </a>
      <a
        className="btn btn-danger btn-xsm mr-5"
        href={userMailLabel?.split('GMAIL')[1]}
        target="_blank"
        rel="noreferrer"
        style={{ width: 100 }}
      >
        <FontAwesomeIcon icon={faGoogle} className="mr-5" />
        Reminder
      </a>
      <a href={`mailto:${userEmail}`}>{userEmail}</a>
    </span>
  );

  return {
    departmentHtml: `<span class="w100 display-ib">${orderRow.departmentId}</span> ${orderRow.departmentName}`,
    branchHtml: `<span class="w100 display-ib">${orderRow.branchId}</span> ${orderRow.branchName}`,
    userHtml: `<span class="w100 display-ib">${orderRow.userCode}</span> ${orderRow.agentName}`,
    userPhoneHtml: `<a href="tel:${user?.home_tel_1}">${user?.home_tel_1 || ''}</a>`,
    branchPhoneHtml: `<a href="tel:${branchPhone}">${branchPhone || ''}</a>`,
    userMailLabel,
    userMailJsx,
  };
};

export const formatSupplierData = (sup: MoSupplier, myOrder: MyOrder): MoSupplierData => {
  const { fullSegment: segment, order } = myOrder;

  const subject = `Bepro Service : REF: ${segment.suppPnrWithOutLock} PnrSeg: ${segment.suppPnrSeg}`;
  const roomsStr = segment.rooms
    .map((room, index) => 'Room ' + (index + 1) + ': ' + room.roomDisplay)
    .join('%0A');
  const namesStrHtl = segment.rooms
    .map((room) => room.paxes.map((pax) => pax.firstName + ' ' + pax.lastName).join('%0A'))
    .join('%0A');

  const namesStrRest = order.paxes.map((pax) => pax.firstName + ' ' + pax.lastName).join('%0A');
  const namesStr = segment.rooms?.length ? namesStrHtl : namesStrRest;
  const checkIn = 'Check in: ' + dateFormat(segment.checkIn, 'd-MMM-yy');

  const id = `%23${order.companyId}.${order.branchId}.${order.userC}.${order.orderId}.${segment.orderSegId}%23`;
  const body = `REF1: ${segment.suppPnrWithOutLock}%0AHotel Name: ${segment.itemDesc
    }%0A${checkIn} Check out: ${dateFormat(
      segment.checkOut,
      'd-MMM-yy'
    )}%0A${roomsStr}%0A%0APassengers names:%0A${namesStr}%0A%0A${id}%0A%0ACompany: ${order.companyId}%0ABranch: ${myOrder?.orderRow?.branchName + ' - ' + order.branchId}%0AUser: ${myOrder?.orderRow?.agentName + ' - ' + myOrder?.orderRow?.userCode}%0ABePro Segment: ${myOrder?.orderRow?.segmentId}`;

  const subject2 = `Bepro Service : REF: ${segment.suppPnrWithOutLock} ${checkIn}`;
  const body2 = `REF: ${segment.suppPnrWithOutLock}%0AHotel Name: ${segment.itemDesc}%0A${checkIn}%0A%0A%0APassengers names:%0A${namesStr}%0A%0ACompany: ${order.companyId}%0ABranch: ${myOrder?.orderRow?.branchName + ' - ' + order.branchId}%0AUser: ${myOrder?.orderRow?.agentName + ' - ' + myOrder?.orderRow?.userCode}%0ABePro Segment: ${myOrder?.orderRow?.segmentId}`;

  const helpDeskMail = `mailto:${sup.helpDeskEmail}?subject=${subject}&body=${body}`;
  const helpDeskGmail = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${sup.helpDeskEmail}&su=${subject}&body=${body}`;
  const emergencyMail = `mailto:${sup.emergencyEmail}?subject=${subject2}&body=${body2}`;
  const emergencyGmail = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${sup.emergencyEmail}&su=${subject2}&body=${body2}`;

  return {
    suppNote: replaceHtmlToStr(replaceHtmlToStr(segment.suppNote)),
    suppKey: segment.rooms?.[0]?.suppKey,
    paidByNote: replaceHtmlToStr(segment.paidBySuppNote),
    external: replaceHtmlToStr(segment.paidBySuppDesc),
    emergencyEmailHtml: `<a href="mailto:${sup.emergencyEmail}">${sup.emergencyEmail}</a>`,
    helpDeskEmailHtml: `<a href="mailto:${sup.helpDeskEmail}">${sup.helpDeskEmail}</a>`,
    emergencyPhoneHtml: `<a href="tel:${sup.emergencyPhone}">${sup.emergencyPhone}</a>`,
    phoneHtml: `<a href="tel:${sup.helpDeskPhone}">${sup.helpDeskPhone}</a>`,
    helpDeskLabel: `${helpDeskMail}GMAIL${helpDeskGmail}`,
    emergencyLabel: `${emergencyMail}GMAIL${emergencyGmail}`,
    helpDeskActions: (
      <div className="mo-mail-actions flex-column flex-gap-5">
        <div className="mo-mail-actions-btns flex-row flex-gap-5">
          <a className="btn btn-primary btn-xsm" href={helpDeskMail}>
            <FontAwesomeIcon icon={faEnvelope} className="mr-5" />
            Support email
          </a>
          <a className="btn btn-danger btn-xsm" href={helpDeskGmail} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faGoogle} className="mr-5" />
            Support Gmail
          </a>
        </div>
        <a href={`mailto:${sup.helpDeskEmail}`}>{sup.helpDeskEmail}</a>
      </div>
    ),
    emergencyActions: (
      <div className="mo-mail-actions flex-column flex-gap-5">
        <div className="mo-mail-actions-btns flex-row flex-gap-5">
          <a className="btn btn-primary btn-xsm" href={emergencyMail}>
            <FontAwesomeIcon icon={faEnvelope} className="mr-5" />
            Emergency email
          </a>
          <a className="btn btn-danger btn-xsm" href={emergencyGmail} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faGoogle} className="mr-5" />
            Emergency Gmail
          </a>
        </div>
        <a href={`mailto:${sup.emergencyEmail}`}>{sup.emergencyEmail}</a>
      </div>
    ),
  };
};

export const formatPaymentTable = (payments: PaymentTrResponse[]): MoTablePaymentData[] => {
  return payments.map((payment) => {
    const result = {
      id: payment.orderId + '.' + payment.orderSegId,
      status: faCcVisa,
      statusHelp: getPayment(payment.status) + '|' + payment.status,
      transactionId: payment.transactionID,
      paymentTransactionResponseId: payment.paymentTransactionResponseId,
      token: showTokenDesc(payment.token),
      numberOfPayments: payment.numberOfPAyments,
      amount: payment.currencyCode + ' ' + payment.amount,
      paymentType: getPaymentTypeIcon(payment.paymentType),
      errCode: payment.errCode + '<br/>' + payment.exErrCode,
      dateC: dateFormat(payment.dateC, 'dd/MM/yyyy HH:mm'),
      reason: payment.reason.replaceAll('*', '</br>*'),
    };
    return result;
  });
};

export const formatRoomTable = (segment: FullSegment) => {
  return segment.rooms.map((room) => {
    const paxNames = room.paxes.map((pax) => pax.fullName).filter(Boolean).join(', ');
    const paxCount = room.paxes.length + '';

    return {
      index: room.roomNumber + ' ',
      room: room.roomDisplay,
      status: room.sysStatus.code,
      paxCount,
      paxCountJsx: paxNames ? (
        <Tooltip content={paxNames}>
          <span>{paxCount}</span>
        </Tooltip>
      ) : (
        <span>{paxCount}</span>
      ),
      paxStr: 'Paxes: ' + room.paxes.length,
    };
  });
};

export const formatSegmentTable = (segment: FullSegment) => {
  return segment.details.map((detail) => {
    let flight = '';
    let arrival = '';
    let arrivalHtml = '';
    if (detail.productTypeCode === 'FLT') {
      flight = `${detail.item.code} ${detail.flightNumber} ${detail.legNumber === 1 ? detail.elapsedTime : ''
        }`;
      arrival = detail.arrival.port.code + ' ' + detail.arrival.port.name;
      arrivalHtml = `<span class="text-bold">${detail.arrival.port.code}</span> ${detail.arrival.port.name}`;
    } else if (detail.productTypeCode === 'HTL') {
      flight = detail.productTypeCode;
      arrival = `${detail.item.name} ${i18n.t('mo.room')} ${detail.itemType.name || detail.board.name
        }`;
      arrivalHtml = `${detail.item.name}</br> ${i18n.t('mo.room')} ${detail.itemType.name || detail.board.name
        }`;
    }

    return {
      pnr: detail.itemPNR,
      flight,
      legs: detail.legNumber + '',
      paxes: detail.numberOfPax + '',
      departure: detail.departure.port.code + ' ' + detail.departure.port.name,
      depClassOfService: detail.departure.classOfService.code,
      departureHtml: `<span class="text-bold">${detail.departure.port.code}<span> ${detail.departure.port.name}`,
      depDateTime: dateFormat(detail.departure.date, 'dd/MMM/yyyy') + ' ' + detail.departure.time,
      arrival,
      arrClassOfService: detail.arrival.classOfService.code,
      arrivalHtml,
      arrDateTime: dateFormat(detail.arrival.date, 'dd/MMM/yyyy') + ' ' + detail.arrival.time,
      status: detail.sysStatus.code + (detail.sysStatus.desc ? ` ${detail.sysStatus.desc}` : ''),
    };
  });
};

export const getMoDetails = (details: MoDetails) => {
  return {
    internalNote: details.internalNote,
    bToken: details.bToken,
    suppKey: details.suppKey,
    publicNote: details.publicNote,
    beproId: details.orderId + '.' + details.orderSegId,
    segDetailId: details.orderSegDetailId,
  };
};

export const formatRemarksTable = (segment?: FullSegment, remarks?: any[], updateRow?: (row: MyOrder) => void) => {
  if (!segment || !remarks) return [];
  return remarks?.map((remark) => {
    let icon: any;

    if (remark.remarkCode === 'APPMAN') {
      icon = faMaxcdn;
    } else if (remark.remarkCode === 'APPYES') {
      icon = faPhoneSquare;
    } else if (remark.remarkCode === 'APPART') {
      icon = faExclamationCircle;
    } else {
      icon = faCheckSquare;
    }

    const requestCanBeClosed = ['APPART', 'APPOPN', 'INTOP'];
    let isClosingRequest = false;
    const closeRequeFn = async (event?: MouseEvent<HTMLButtonElement>) => {
      if (isClosingRequest) return;
      isClosingRequest = true;
      const button = event?.currentTarget;
      if (button) button.disabled = true;

      try {
        const res = await closeRequest({
          orderId: segment.orderId,
          segmentId: segment.orderSegId,
          remarkId: remark.orderSegRemarkId,
        });

        if (res.myOrder) {
          updateRow?.(res.myOrder);
          showSuccess('Request closed successfully');
        }
      } finally {
        isClosingRequest = false;
        if (button) button.disabled = false;
      }
    };
    const isClosed = remark.remarkDesc?.includes('Closed');
    const closeReqJsx = (
      <Fragment>
        {requestCanBeClosed.includes(remark.remarkCode) && !isClosed && (
          <Button title="Close Request" onClick={closeRequeFn} disabled={isClosingRequest} className="btn btn-danger btn-sm" />
        )}
        {isClosed && <span className="">Request closed</span>}
      </Fragment>
    )

    return {
      remarkType: remark.remarkCode,
      remarkCodeHelp: 'text-light text-xsm|' + remark.remarkCode,
      remarkCode: icon,
      orderSegRemarkId: remark.orderSegRemarkId,
      desc: remark.remarkDesc ? remark.remarkDesc.split('===')[0] : '',
      user:
        remark.remarkDesc && remark.remarkDesc.includes('===') && remark.remarkDesc.includes('|')
          ? remark.remarkDesc.split('===')[1].split('|')[0]
          : remark.remarkDesc,
      dateC: remark.dateC,
      closeReq: closeReqJsx,
    };
  });
};

export const formatDocTable = (
  documents: Document[],
  companyId: number,
  onDocumentDeleted?: (documentId: number) => void
) => {
  return documents.map((doc) => {
    const additionalStr = `${companyId}/${doc.orderId}/${doc.orderSegId}/`;
    const href = `/preview?srt=${doc.documentStr.includes(`/${doc.orderSegId}/`)
      ? doc.documentStr
      : additionalStr + doc.documentStr
      }`;
    const documentDownload = () => {
      const params = {
        orderId: doc.orderId,
        segId: doc.orderSegId,
        documentId: doc.orderDocumentId,
      };
      downloadDocument(params, doc.documentName);
    };
    return {
      fileName: doc.documentName,
      type: doc.documentType,
      open: doc.documentStr,
      documentId: doc.orderDocumentId,
      dateC: dateFormat(doc.dateC, 'dd/MM/yyyy '),
      openHtml: doc.documentType.includes('image') ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="btn btn-link btn-ssm"
          style={{ display: 'inline' }}
        >
          Open
        </a>
      ) : (
        <Button title="Download" onClick={documentDownload} className="btn btn-link btn-ssm" />
      ),
      delete: 'Detele',
      deleteHtml: doc.documentName.includes('Reservations screenshot') ? (
        ''
      ) : (
        <MoDeleteDocument
          orderId={doc.orderId}
          segId={doc.orderSegId}
          documentId={doc.orderDocumentId}
          onDocumentDeleted={onDocumentDeleted}
        />
      ),
    };
  });
};

const getPaymentTypeIcon = (ptype: string) => {
  switch (ptype) {
    case 'CreditCard':
      return faCreditCard;
    case 'CreditCardAith':
      return faCreditCardAlt;
    case 'BankTransfer':
      return faRightLeft;
    case 'Cash':
      return faMoneyBillAlt;
    case 'Cheque':
      return faList;
    default:
      return '';
  }
};

const showTokenDesc = (token: string): string => {
  if (!token) return '';

  let html = '';
  const arr = token.split('‡');

  if (arr.length > 0) html += `Code - ${arr[0]}`;

  if (arr.length > 1) html += '<br/>Token - ' + arr[1];

  if (arr.length > 2) html += '<br/>Approve Number - ' + arr[2];

  if (arr.length > 3) html += '<br/>Order Id - ' + arr[3];

  if (arr.length > 4) html += '<br/>Valid Date - ' + arr[4];

  if (arr.length > 5) html += '<br/>Customer Id - ' + arr[5];

  return html;
};

export const orderGroupColors = [
  'lightgray',
  'lightblue',
  'palegreen',
  'lightpink',
  'beige',
  'mintcream',
  'lavender',
  'peachpuff',
  'honeydew',
  'lightcoral',
];

export const orderGroupColorsDark = [
  '#4b5563',
  '#1e3a5f',
  '#14532d',
  '#7f1d1d',
  '#78350f',
  '#064e3b',
  '#4c1d95',
  '#9a3412',
  '#365314',
  '#9f1239',
];

export const getOrderGroupColors = () => {
  const scheme =
    document.documentElement.style.colorScheme ||
    getComputedStyle(document.documentElement).colorScheme;
  return scheme === 'dark' ? orderGroupColorsDark : orderGroupColors;
};

export const markOrdersWithSameId = (items: MyOrder[]) => {

  const ids = items.map((item) => item.orderRow.orderId);
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);

  const result = items.map((item) => {
    return {
      ...item,
      isSameIdColor: duplicateIds.includes(item.orderRow.orderId)
        ? (() => {
            const colors = getOrderGroupColors();
            return colors[duplicateIds.indexOf(item.orderRow.orderId) % colors.length];
          })()
        : '',
    };
  });

  return result;
};


const isNotEmptyValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const getChangeDescString = (changeDesc: unknown): string => {
  if (changeDesc == null) return '';
  if (typeof changeDesc === 'string') return changeDesc;
  return String(changeDesc);
};



export const formatMyOrder = (item: MyOrder): MoTableData => {
  const clx = dateFormat(item.orderRow.roomsFirstCXL, 'dd/MM/yy');

  const appYes = item.fullSegment.remarks.find((remark) => remark.remarkCode === 'APPYES');
  const appAgt = item.fullSegment.remarks.find((remark) => remark.remarkCode.includes('AGT'));
  const appOpn = item.fullSegment.remarks.filter((remark) => remark.remarkCode === 'APPOPN');
  const appArt = item.fullSegment.remarks.filter((remark) => remark.remarkCode === 'APPART');
  const isClosedAppOpn = appOpn.every((remark) => remark.remarkDesc.includes('Closed'));
  const isClosedAppArt = appArt.every((remark) => remark.remarkDesc.includes('Closed'));

  const data = {
    appYes: '',
    appYesHtml: appYes ? <FontAwesomeIcon size='lg' icon={faPhoneSquare} color="var(--color-text)" /> : '',
    appOpn: '',
    appOpnHtml: appOpn.length ? (
      <FontAwesomeIcon
        size='lg'
        icon={faCheckSquare}
        color={isClosedAppOpn ? 'var(--color-text)' : 'var(--color-success)'}
      />
    ) : (
      ''
    ),
    appArt: '',
    appArtHtml: appArt.length ? (
      <FontAwesomeIcon
        size='lg'
        icon={faExclamationCircle}
        color={isClosedAppArt ? 'var(--color-text)' : 'var(--color-success)'}
      />
    ) : (
      ''
    ),
    appAgt: '',
    appAgtHtml: appAgt ? <FontAwesomeIcon size='lg' icon={faUser} color="var(--color-text)" /> : '',
  };

  const name = item.orderRow.mainDisplayPax
    ?.toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const allNames = item.order.paxes.map((pax) => pax.fullDisplayName).join(' ');
  const itemsText = item.fullSegment.itemDesc?.toUpperCase() || '';
  const changeDescStr = getChangeDescString(item.orderRow.changeDesc);
  const param5Str = item.order.param_5 != null ? String(item.order.param_5) : '';
  const isBookingMonitor = isNotEmptyValue(item.order.param_5) && param5Str.indexOf('‡') > 0;

  return {
    ...data,
    id: item.orderRow.orderId,
    segmentId: item.orderRow.segmentId,
    docket: item.orderRow.docket,
    suppPnr: item.fullSegment.suppPnr,
    orderRow: item.orderRow,
    suppPnrJsx: (
      <span>
        {item.fullSegment.suppPnr.includes('BPRLOCK:') ? (
          <FontAwesomeIcon className="mr-5" icon={faLock} />
        ) : (
          ''
        )}
        {item.fullSegment.suppPnr.replace('BPRLOCK:', '')}
      </span>
    ),
    checkIn: dateFormat(item.fullSegment.checkIn, 'dd/MM/yy'),
    checkInDate: new Date(item.fullSegment.checkIn),
    checkOut: dateFormat(item.fullSegment.checkOut, 'dd/MM/yy'),
    checkOutDate: new Date(item.fullSegment.checkOut),
    name: name,
    nameHtml: `<span ${name?.toLowerCase().includes('#t#') ? 'class="color-danger"' : ''}>${name}</span>`,
    dest: item.fullSegment.suppCityDesc || item.fullSegment.sysCityDesc,
    items: itemsText,
    itemsJsx: (
      <span>
        {item.orderRow.manualPNR && (
          <FontAwesomeIcon className="mr-5" icon={faHand} color="var(--color-danger)" title="Manual PNR" />
        )}
        {item.orderRow.haveNotes && (
          <FontAwesomeIcon className="mr-5" icon={faList} color="var(--color-danger)" title="Notes" />
        )}
        {isNotEmptyValue(item.orderRow.changeDesc) && (
          <FontAwesomeIcon
            className="mr-5"
            icon={faExclamationCircle}
            color={changeDescStr.includes('HZ‡New Segment:') ? 'var(--color-danger)' : 'var(--color-success)'}
            title={changeDescStr}
          />
        )}
        {isBookingMonitor && (
          <FontAwesomeIcon
            className="mr-5"
            icon={faDesktop}
            color="var(--color-danger)"
            title="Order From Booking Monitor"
          />
        )}
        {itemsText}
      </span>
    ),
    clx: clx,
    clxDate: new Date(item.orderRow.roomsFirstCXL),
    clxHtml: `<span class="clx">${clx}</span>`,
    status: item.orderRow.status,
    voucher: item.orderRow.voucher,
    quantity: item.orderRow.quantity,
    total: item.orderRow.price
      ? Math.round(item.orderRow.price)?.toLocaleString() + '' + item.orderRow.currency
      : '',
    totalNumber: item.orderRow.price,
    supplier: item.fullSegment.sysSuppDesc,
    supplierCode: item.fullSegment.sysSuppCode,
    agent: item.orderRow.agentName,
    branch: item.orderRow.branchName,
    productType: item.orderRow.productType,
    paymentType: item.orderRow.paidStatus,
    segmentColor: item.isSameIdColor,
    ticketNumber: item.order.hp_Ticket,
    allNames,
    country: (
      <Fragment>
        {!!item.fullSegment.countryCode && (
          <span>
            <ReactCountryFlag countryCode={item.fullSegment.countryCode} svg className="mr-5" />
            {item.fullSegment.countryCode}
          </span>
        )}
      </Fragment>
    ),
    dateC: dateFormat(item.orderRow.dateC, 'dd/MM/yy'),
    dateCJsx: <span>{item.fullSegment.saveReport?.sr_original_amount ? <FontAwesomeIcon icon={faBitcoinSign} /> : ''} {dateFormat(item.orderRow.dateC, 'dd/MM/yy')}</span>,
    orderId: item.orderRow.orderId,
    visible: true,
    rqOk: item.orderRow.isCurrentRqOk,
    isDup: item.orderRow.isDuplicate,
    manualPnr: item.orderRow.manualPNR,
    haveNotes: item.orderRow.haveNotes,
    haveDocs: item.orderRow.haveDocs,
    haveSRep: item.orderRow.haveSaveReport,
    segmentWasChangeDesc: !!item.fullSegment.segmentWasChangeDesc,
    original: item.fullSegment.saveReport?.sr_original_amount,
    refund: item.fullSegment.saveReport?.sr_refund_amount,
    refundNc: item.fullSegment.saveReport?.sr_refund_nc_amount,
    product: item.orderRow.productType,
    department: item.orderRow.departmentId + '',
    userCode: item.orderRow.userCode + '',
    segCount: item.order.segments.length,
    nights: item.orderRow.nights,
  };
};


export function tryFormatJson(strOrObj: any) {
  if (typeof strOrObj === 'string') {
    try {
      const obj = JSON.parse(strOrObj);
      return JSON.stringify(obj, null, 2);
    } catch {
      return strOrObj;
    }
  }
  return String(strOrObj);
}


export interface SearchFilters {
  orderSupplierPNR?: string;
  orderBackOfficeId?: string;
  orderPaxName?: string;
  orderItem?: string;
}

export const matchesSearchFilters = (order: MyOrder, filters: SearchFilters): boolean => {
  const toSearchableString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.toLowerCase().trim();
    if (typeof value === 'number') return value.toString();
    return String(value).toLowerCase().trim();
  };

  // Filter by Supplier PNR
  if (filters.orderSupplierPNR) {
    const searchTerm = toSearchableString(filters.orderSupplierPNR);
    const suppPnr = toSearchableString(order.fullSegment.suppPnr);
    if (!suppPnr.includes(searchTerm)) return false;
  }

  // Filter by Back Office ID
  if (filters.orderBackOfficeId) {
    const searchTerm = toSearchableString(filters.orderBackOfficeId);
    const backOfficeId = toSearchableString(order.fullSegment.backOfficeId);
    if (!backOfficeId.includes(searchTerm)) return false;
  }

  // Filter by Passenger Name
  if (filters.orderPaxName) {
    const searchTerm = toSearchableString(filters.orderPaxName);
    let found = false;

    // Check main order passengers
    if (order.order.paxes && order.order.paxes.length > 0) {
      found = order.order.paxes.some((pax) => {
        const fullName = toSearchableString(pax.fullDisplayName || `${pax.firstName} ${pax.lastName}`);
        return fullName.includes(searchTerm);
      });
    }

    // Check room passengers if not found in main order
    if (!found && order.fullSegment.rooms && order.fullSegment.rooms.length > 0) {
      found = order.fullSegment.rooms.some((room) => {
        if (!room.paxes || room.paxes.length === 0) return false;
        return room.paxes.some((pax) => {
          const fullName = toSearchableString(pax.fullDisplayName || `${pax.firstName} ${pax.lastName}`);
          return fullName.includes(searchTerm);
        });
      });
    }

    if (!found) return false;
  }

  // Filter by Item Description
  if (filters.orderItem) {
    const searchTerm = toSearchableString(filters.orderItem);
    const itemDesc = toSearchableString(order.fullSegment.itemDesc);
    if (!itemDesc.includes(searchTerm)) return false;
  }

  return true;
};

export const matchesTableDataSearchFilters = (tableData: MoTableData, filters: SearchFilters): boolean => {
  const toSearchableString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.toLowerCase().trim();
    if (typeof value === 'number') return value.toString();
    return String(value).toLowerCase().trim();
  };

  // Filter by Supplier PNR
  if (filters.orderSupplierPNR) {
    const searchTerm = toSearchableString(filters.orderSupplierPNR);
    const suppPnr = toSearchableString(tableData.suppPnr);
    if (!suppPnr.includes(searchTerm)) return false;
  }

  // Filter by Back Office ID - using docket field
  if (filters.orderBackOfficeId) {
    const searchTerm = toSearchableString(filters.orderBackOfficeId);
    const docket = toSearchableString(tableData.docket);
    if (!docket.includes(searchTerm)) return false;
  }

  // Filter by Passenger Name
  if (filters.orderPaxName) {
    const searchTerm = toSearchableString(filters.orderPaxName);
    const paxName = toSearchableString(tableData.allNames);
    if (!paxName.includes(searchTerm)) return false;
  }

  // Filter by Item Description
  if (filters.orderItem) {
    const searchTerm = toSearchableString(filters.orderItem);
    const itemDesc = toSearchableString(tableData.items);
    if (!itemDesc.includes(searchTerm)) return false;
  }

  return true;
};
